import React, {
    useMemo,
    createContext,
    PropsWithChildren,
    useContext,
} from 'react'
import {
    ChatFieldsFragment,
    ChatMessageFieldsFragment,
    MessageOrigin,
} from '~graphql/generated/graphql'
import { checkIfLastMessageWasSame } from './message-helpers'
import { MessageProps } from './types'
import { isSystemMessage } from '~components/chats/chat/helpers'

export default function MessagePropertiesProvider({
    message,
    messageIndex,
    allMessages,
    chat,
    refetchMessages,
    children,
}: PropsWithChildren<
    MessageProps & {
        refetchMessages: () => void
    }
>) {
    return (
        <MessagePropertiesContext.Provider
            value={{
                ...useMessagePropertyValues({
                    message,
                    messageIndex,
                    allMessages,
                    refetchMessages,
                    chat,
                }),
            }}
        >
            {children}
        </MessagePropertiesContext.Provider>
    )
}

function useMessagePropertyValues({
    message,
    messageIndex,
    allMessages,
    refetchMessages,
    chat,
}: MessageProps & {
    refetchMessages: () => void
}): MessagePropertiesContextType {
    const lastMessageTime = useMemo(
        () => allMessages[messageIndex - 1]?.created_at,
        [allMessages, messageIndex],
    )

    const LAST_MESSAGE_TIME_THRESHOLD_LG = 1000 * 60 * 15 // 15 minutes
    const LAST_MESSAGE_TIME_THRESHOLD_SM = 1000 * 60 * 5 // 5 minutes

    const messageTimeDifference = useMemo(
        () => message.created_at - lastMessageTime,
        [message, lastMessageTime],
    )

    const isPastLgThreshold = useMemo(
        () => messageTimeDifference > LAST_MESSAGE_TIME_THRESHOLD_LG,
        [messageTimeDifference],
    )

    const isPastSmThreshold = useMemo(
        () => messageTimeDifference > LAST_MESSAGE_TIME_THRESHOLD_SM,
        [messageTimeDifference],
    )

    const isLast = useMemo(
        () => messageIndex === allMessages.length - 1,
        [messageIndex, allMessages],
    )
    const isFirst = useMemo(() => messageIndex === 0, [messageIndex])

    const isSystemMsg = useMemo(() => isSystemMessage(message), [message])

    const isCurrentUser = useMemo(
        () =>
            !!message.clinic_user ||
            message.message_origin === MessageOrigin.ChckupDashboard,
        [message],
    )

    const noHead = useMemo(
        () =>
            !!allMessages[messageIndex - 1] &&
            checkIfLastMessageWasSame(allMessages[messageIndex - 1], message) &&
            message.created_at - allMessages[messageIndex - 1]?.created_at <
                LAST_MESSAGE_TIME_THRESHOLD_SM,
        [allMessages, messageIndex],
    )

    const noTail = useMemo(
        () =>
            !isLast &&
            allMessages[messageIndex + 1]?.client?.id === message.client?.id &&
            allMessages[messageIndex + 1]?.created_at - message.created_at <
                LAST_MESSAGE_TIME_THRESHOLD_LG,
        [isLast, allMessages, messageIndex, message],
    )

    const showName = useMemo(
        () =>
            allMessages[messageIndex - 1]?.client?.id !== message.client?.id ||
            allMessages[messageIndex - 1]?.clinic_user?.id !==
                message.clinic_user?.id,
        [allMessages, messageIndex],
    )

    return {
        message,
        messageIndex,
        allMessages,
        chat,
        refetchMessages,

        isPastLgThreshold,
        isPastSmThreshold,
        isLast,
        isFirst,
        isCurrentUser,
        noHead,
        noTail,
        showName,
        isSystemMsg,
    }
}

type MessagePropertiesContextType = {
    // Content values
    message: ChatMessageFieldsFragment
    messageIndex: number
    allMessages: ChatMessageFieldsFragment[]
    chat: ChatFieldsFragment

    refetchMessages: () => void

    // Message properties
    isPastLgThreshold: boolean
    isPastSmThreshold: boolean
    isLast: boolean
    isFirst: boolean
    isCurrentUser: boolean
    noHead: boolean
    noTail: boolean
    showName: boolean
    isSystemMsg: boolean
}

const MessagePropertiesContext = createContext<MessagePropertiesContextType>(
    {} as MessagePropertiesContextType,
)

export function useMessageProperties() {
    return useContext(MessagePropertiesContext)
}
